/**
 * Lightweight analytics wrapper.
 * Pushes every event to BOTH:
 *   1. window.dataLayer  (for GTM → GA4)
 *   2. PostHog            (existing wrapper at src/lib/posthog.ts — safe Proxy
 *                          that no-ops until PostHog finishes initializing)
 *
 * Hard rules:
 *   - Never throws. Tracking failures must never break the page.
 *   - Never sends PII (no names, emails, phones, payment data, user IDs,
 *     student IDs, academy IDs, or tenant-sensitive data).
 *   - Whitelisted props only: page_path, article_slug, article_category,
 *     cta_type, plan_code, source_page, language, etc.
 */
import { posthog } from "./posthog";

export type AnalyticsProps = Record<string, string | number | boolean | null | undefined>;

export function track(event: string, props?: AnalyticsProps) {
  const payload = props || {};
  // 1) GTM / GA4
  try {
    if (typeof window !== "undefined") {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({ event, ...payload });
    }
  } catch {
    // never throw from analytics
  }
  // 2) PostHog (Proxy no-ops until init resolves)
  try {
    (posthog as any).capture?.(event, payload);
  } catch {
    // never throw from analytics
  }
}

export const analytics = {
  // Course funnel (existing)
  lessonCompleted: (props: AnalyticsProps) => track("lesson_completed", props),
  quizStarted: (props: AnalyticsProps) => track("quiz_started", props),
  quizCompleted: (props: AnalyticsProps) => track("quiz_completed", props),
  courseFinished: (props: AnalyticsProps) => track("course_finished", props),

  // Marketing / SEO funnel — safe, non-PII props only.
  // blog_article_view = article page view on /blog/:slug
  blogArticleView: (props?: AnalyticsProps) => track("blog_article_view", props),
  // article_cta_click = CTA click inside an individual article (/blog/:slug)
  // Future: blog_card_click for clicks on cards in the /blog index.
  articleCtaClick: (props?: AnalyticsProps) => track("article_cta_click", props),
  pricingView: (props?: AnalyticsProps) => track("pricing_view", props),
  signupCtaClick: (props?: AnalyticsProps) => track("signup_cta_click", props),
  signupStart: (props?: AnalyticsProps) => track("signup_start", props),
  academyCreationStart: (props?: AnalyticsProps) => track("academy_creation_start", props),
  academyCreationComplete: (props?: AnalyticsProps) => track("academy_creation_complete", props),
  planSelected: (props?: AnalyticsProps) => track("plan_selected", props),
  contactClick: (props?: AnalyticsProps) => track("contact_click", props),
};
